import { toUtcMidnight } from '@nuxt-shared/lib/chart-format';
import type {
  ChartApiPoint,
  ChartApiSeries,
  ChartPoint,
  ChartSeries,
  ChartWidgetTab,
  ChartZone,
} from '../types';

/**
 * Построение рядов графика — порт легаси
 * `components/aton/chart/templates/area.detail_v2024/script.js`
 * (расчёт `cumprodLast` и `buildNonTradeZones`).
 */

/** Цвет первой линии: `LINE_COLOR` из прод-конфигов графиков в публикациях. */
export const LINE_COLOR = '#7000FF';

/** Вторая и далее линии — хвост палитры легаси `arLineColors.__light`. */
const EXTRA_COLORS = [
  '#92a8d1',
  '#486db5',
  '#3bbdaa',
  '#d8003f',
  '#ffc000',
  '#6f90a7',
];

/** Цвет участка без торгов — `buildNonTradeZones` легаси. */
const NON_TRADE_COLOR = '#B3B3B3';

/** Округление до пяти знаков — `cumprodLast.toFixed(5)` легаси. */
const round5 = (value: number): number => Math.round(value * 1e5) / 1e5;

/** Цвет ряда по его порядковому номеру. */
export const seriesColor = (index: number): string =>
  index === 0
    ? LINE_COLOR
    : (EXTRA_COLORS[(index - 1) % EXTRA_COLORS.length] as string);

/**
 * Точки ряда.
 *
 * Для доходности стоимость пая корректируется на выплаты нарастающим итогом —
 * этот же расчёт легаси делает перед отправкой данных в Highcharts, проценты
 * дальше считает сам Highcharts (`compare: 'percent'`). Для СЧА значения идут
 * как есть.
 */
export function buildPoints(
  values: ChartApiPoint[],
  tab: ChartWidgetTab,
): ChartPoint[] {
  const isScha = tab === 'scha';
  let cumulative = 1;
  let lastPrice = 0;
  let isFirstStep = true;

  return values.reduce<ChartPoint[]>((acc, item) => {
    const x = toUtcMidnight(String(item.date));

    if (x === null) return acc;

    const price = Number(item.price) || 0;
    const payment = Number(item.payment) || 0;
    const isTrade = item.is_trade !== false;

    if (isScha) {
      acc.push({ x, y: Number(item.scha) || 0, price, isTrade });
      return acc;
    }

    // Поправка берётся до учёта текущей выплаты — порядок как в легаси.
    const corrected = price * cumulative;
    const base = price > 0 ? price : lastPrice;

    if (price > 0) lastPrice = price;

    if (base > 0) {
      const step = (base + payment) / base;
      cumulative = isFirstStep && payment ? step : cumulative * step;
      cumulative = round5(cumulative);
    }

    isFirstStep = false;

    acc.push({ x, y: corrected, price, isTrade });

    return acc;
  }, []);
}

/**
 * Участки без торгов (`is_trade: false`).
 *
 * Точки участка интерполируются на прямую между крайними торговыми точками —
 * отрезок рисуется ровным, как в макете, — и красятся серым через
 * `series.zones`. Даты этих точек возвращаются отдельно: по ним тултип
 * переключается на текст «торги не проводились».
 *
 * Массив точек меняется на месте: он уже локальная копия ряда, а на расчёт
 * доходности значения не влияют — в легаси ровно так же.
 */
export function buildNonTradeZones(
  points: ChartPoint[],
  color: string,
): { zones: ChartZone[]; nonTradeDates: Set<number> } {
  const zones: ChartZone[] = [];
  const nonTradeDates = new Set<number>();
  const total = points.length;
  let index = 0;

  while (index < total) {
    const point = points[index];

    if (!point || point.isTrade) {
      index++;
      continue;
    }

    const start = index;

    while (index < total && points[index] && !points[index]?.isTrade) {
      nonTradeDates.add((points[index] as ChartPoint).x);
      index++;
    }

    // [start..end] — непрерывный участок без торгов.
    const end = index - 1;
    const left = start > 0 ? points[start - 1] : null;
    const right = end + 1 < total ? points[end + 1] : null;

    if (left && right && right.x !== left.x) {
      for (let i = start; i <= end; i++) {
        const current = points[i] as ChartPoint;
        const ratio = (current.x - left.x) / (right.x - left.x);

        current.y = left.y + (right.y - left.y) * ratio;
      }

      // Серым красится весь отрезок от последней торговой точки до первой
      // следующей — оба соединяющих сегмента.
      zones.push({ value: left.x, color });
      zones.push({ value: right.x, color: NON_TRADE_COLOR });
      continue;
    }

    // Участок у края данных — тянем плоско к имеющейся границе.
    const base = left ?? right;

    if (!base) continue;

    for (let i = start; i <= end; i++) {
      (points[i] as ChartPoint).y = base.y;
    }

    zones.push({ value: (left ?? (points[start] as ChartPoint)).x, color });
    zones.push({
      value: (right ?? (points[end] as ChartPoint)).x,
      color: NON_TRADE_COLOR,
    });
  }

  // Хвост после последней зоны рисуется основным цветом.
  if (zones.length) zones.push({ color });

  return { zones, nonTradeDates };
}

/** Ряд для Highcharts из одного графика ответа API. */
export function buildSeries(
  code: string,
  apiSeries: ChartApiSeries,
  index: number,
  tab: ChartWidgetTab,
): ChartSeries {
  const color = seriesColor(index);
  const points = buildPoints(apiSeries.values ?? [], tab);
  const { zones, nonTradeDates } = buildNonTradeZones(points, color);

  return {
    code,
    // Человекочитаемых названий рядов API не отдаёт — в легенде остаётся код.
    name: code,
    color,
    points,
    zones,
    nonTradeDates,
  };
}
